import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { http } from "../../../../../../../../services/HttpService";
import { HTTP_ENDPOINTS } from "../../../../../../../../consts/api";
import { useInvoice } from "../../../../../hooks/invoice/useInvoice";
import { uiNotification } from "../../../../../../../../services/UINotificationService";
import { useAppTranslation } from "../../../../../../../../i18n/useAppTranslation";
import { dispatch } from "../../../../../../../../store/store";
import { checkoutInvoice } from "../../../../../../../../store/checkoutInvoice";
import { useCloseSmartReader } from "./useCloseSmartReader";
import { useSmartReaderState } from "./useSmartReaderState";

export const useCardReaderCharge = (selectedReader) => {
  const { refetchInvoice, invoice } = useInvoice();
  const { tSales } = useAppTranslation.Sales();
  const [isPolling, setIsPolling] = useState(false);

  const { mutate: closeSmartReader } = useCloseSmartReader();
  const { mutateAsync: getSmartReaderState } = useSmartReaderState(
    selectedReader?.readerIdentifier,
  );

  const req = useMutation(
    (dto) => http.post(HTTP_ENDPOINTS.postCheckoutPayment(), dto),
    {
      onSuccess: () => {
        uiNotification.success(
          tSales("checkoutInvoice.success.transferPaymentToReader"),
        );

        let unchangedIterations = 0;
        const maxUnchangedIterations = 60;
        const surchargeBeforePayment = invoice?.surcharge || 0;
        const paymentsBeforeCharge =
          invoice?.paymentTransaction?.payments?.length || 0;
        let isStopped = false;
        let isPollInFlight = false;

        const pollInvoiceDetails = async () => {
          if (isStopped || isPollInFlight) return;
          isPollInFlight = true;
          try {
            const response = await getSmartReaderState();
            const state = response?.data?.data;

            // Reader reported a failed action: show Stripe's reason, not a generic error
            if (state?.failed) {
              clearPolling(
                state.error ||
                  response?.data?.message ||
                  tSales("checkoutInvoice.failed.paymentFailed"),
              );
              return;
            }

            if (!state?.wait) {
              clearPolling();
            } else {
              unchangedIterations++;
              if (unchangedIterations >= maxUnchangedIterations) {
                // The payment may have succeeded while the reader is still busy
                // (e.g. the customer is signing). Check the invoice before failing.
                const invoiceResult = await refetchInvoice();
                const paymentsAfter =
                  invoiceResult?.data?.paymentTransaction?.payments?.length ||
                  0;
                if (paymentsAfter > paymentsBeforeCharge) {
                  clearPolling();
                  return;
                }
                closeSmartReader(selectedReader.readerIdentifier);
                clearPolling(tSales("checkoutInvoice.failed.paymentFailed"));
                return;
              }
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
          } finally {
            isPollInFlight = false;
          }
        };

        const pollingInterval = setInterval(pollInvoiceDetails, 3000);

        const clearPolling = async (errorMessage = null) => {
          isStopped = true;
          clearInterval(pollingInterval);
          setIsPolling(false);
          const refetchResult = await refetchInvoice();
          if (
            surchargeBeforePayment > 0 &&
            (refetchResult?.data?.surcharge || 0) < surchargeBeforePayment
          ) {
            uiNotification.info(
              "Surcharge fees were adjusted based on your payment method and processing rates. Your total has been updated",
            );
          }
          if (errorMessage) uiNotification.error(errorMessage);
          dispatch(checkoutInvoice.actions.paymentViewClose());
        };

        setIsPolling(true);
      },
      onError: (error) => {
        uiNotification.error(error.response?.data?.message);
      },
    },
  );

  return {
    chargePayment: req.mutate,
    isLoading: req.isLoading,
    isPolling,
  };
};
